import { StatusId, TabKind, TabDescriptor, CommandeMS } from '../types';

// Logique pure des onglets de la case PRESTATAIRE x MS. Onglets FIXES (le presta ne voit que sa
// propre candidature). Gating 100% id-based, fidele a l'oracle getDisabledTabs MS presta
// (SuiviDeCommande.tsx:114-177) :
//   - Notifications et signatures : grisee tant que status.id < 8 (phase signature presta ouverte).
//   - Messagerie + Evaluation    : grisees tant que status.id !== 11 (Valider = signe des 2 cotes).
//   - Historique / Candidature / Q&R : actives (le canal Q&R reste ouvert cote MS, contrairement
//     au BC ou Messagerie/Evaluation sont des placeholders "a venir").

// Statut effectif de la commande (table `status` partagee), lu par id.
const statusId = (commande: CommandeMS): number | undefined =>
  commande.status?.id ?? commande.id_status;

// Ordre des onglets = stable (cle de ?tab=<index>), calque sur l'oracle presta MS.
const TAB_DEFS: { kind: TabKind; label: string }[] = [
  { kind: 'historique', label: 'Historique' },
  { kind: 'consultation', label: 'Consultation' },
  { kind: 'questions_reponses', label: 'Questions et réponses' },
  { kind: 'candidature', label: 'Offre' },
  { kind: 'notifications', label: 'Notifications et signatures' },
  { kind: 'messagerie', label: 'Messagerie' },
  { kind: 'evaluation', label: 'Évaluation' },
];

// Seuil d'ouverture de l'onglet signature (phase signature presta) : statut >= 8.
const NOTIFICATIONS_MIN_STATUS = StatusId.EnAttenteSignaturePrestataire; // 8

const tabDisabled = (kind: TabKind, commande: CommandeMS): boolean => {
  const sid = statusId(commande) ?? 0;
  switch (kind) {
    case 'historique':
    case 'consultation':
    case 'candidature':
    case 'questions_reponses':
      return false;
    case 'notifications':
      // Phase signature ouverte a partir de >= 8, SAUF statut terminal Expirer (12) : un MS expire
      // (delai depasse sans signature) ne doit jamais exposer l'onglet Notifications et signatures (bug).
      return sid < NOTIFICATIONS_MIN_STATUS || sid === StatusId.Expirer;
    case 'messagerie':
    case 'evaluation':
      return sid !== StatusId.Valider;
    default:
      return true;
  }
};

// Cote presta MS, aucun onglet "a venir" : tout est reel (Q&R, Messagerie, Evaluation actifs).
// DIVERGENCE VOLONTAIRE de l'oracle : getInactiveTabs (SuiviDeCommande.tsx:168-169) poussait
// Evaluation en `inactive` INCONDITIONNELLEMENT (toujours grisee "en cours de developpement"),
// tout en rendant pourtant un vrai CommandeEvaluationTab (l.867-876, validated = status 11) :
// incoherence de l'oracle. On la leve comme cote BC V2 -> Evaluation est un onglet REEL, gate par
// `disabled` (sid !== 11), pas marque inactive. (Si une parite stricte "Evaluation jamais
// accessible" etait voulue, il faudrait renvoyer true pour 'evaluation' ici.)
const tabInactive = (_kind: TabKind): boolean => false;

// Un onglet est ATTEIGNABLE quand il n'est ni grise ni inactif — exactement le predicat qui gate la
// barre d'onglets et le `goToTab` du shell. Expose pour que les CTA de bascule (commandeMsNextTab)
// se masquent sur LA MEME regle : sans ca, un bouton pourrait pointer une destination que goToTab
// refuse d'ouvrir, et l'utilisateur cliquerait dans le vide.
export const isTabReachable = (commande: CommandeMS, kind: TabKind): boolean =>
  !tabDisabled(kind, commande) && !tabInactive(kind);

export const buildCommandeMsTabs = (commande: CommandeMS): TabDescriptor[] =>
  TAB_DEFS.map((def) => ({
    key: def.kind,
    kind: def.kind,
    label: def.label,
    disabled: tabDisabled(def.kind, commande),
    inactive: tabInactive(def.kind),
  }));

// Onglet d'ouverture par defaut (hors ?tab=), selon le statut le plus pertinent du moment :
// - A candidater (3) -> Candidature (l'action attendue du presta).
// - phase signature (8/9/10/13) ou signe (11) -> Notifications et signatures.
// - sinon (en attente analyse, refus, expire, annule...) -> Historique.
// AMELIORATION VOLONTAIRE vs oracle : l'oracle ouvrait TOUJOURS sur Historique (index 0, sauf
// ?tab=) ; ici on amene le presta directement sur l'onglet pertinent. computeDefaultTabIndex
// re-verifie !disabled && !inactive (fallback index 0), donc on ne propose jamais un onglet grise.
export const computeDefaultTabKind = (commande: CommandeMS): TabKind => {
  switch (statusId(commande)) {
    case StatusId.ACandidater:
      return 'candidature';
    case StatusId.EnAttenteSignaturePrestataire:
    case StatusId.EnAttenteSignatureAcheteur:
    case StatusId.ASignerPrestataire:
    case StatusId.ANotifier:
    case StatusId.Valider:
      return 'notifications';
    default:
      return 'historique';
  }
};

export const computeDefaultTabIndex = (
  descriptors: TabDescriptor[],
  commande: CommandeMS,
): number => {
  const kind = computeDefaultTabKind(commande);
  const index = descriptors.findIndex((d) => d.kind === kind && !d.disabled && !d.inactive);
  return index >= 0 ? index : 0;
};
